/**
 * CalendarPanel -- calendar with day/week/month view modes.
 *
 * Features:
 * - View mode toggle (Day/Week/Month)
 * - Compact day cells in month view
 * - Navigation between periods
 * - Click day/item to see details
 */

import { useState, useMemo } from "react";
import type { SidebarTask, SidebarEvent } from "./types";

interface CalendarPanelProps {
  tasks: SidebarTask[];
  events: SidebarEvent[];
  onTaskClick: (task: SidebarTask) => void;
  onEventClick: (event: SidebarEvent) => void;
  /** Called when calendar navigation changes the visible date range */
  onDateRangeChange?: (start: string, end: string) => void;
}

type ViewMode = "day" | "week" | "month";

type DayItem =
  | { type: "task"; data: SidebarTask }
  | { type: "event"; data: SidebarEvent };

const WEEKDAYS_SHORT = ["S", "M", "T", "W", "T", "F", "S"];
const WEEKDAYS_FULL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getMonthDays(year: number, month: number): Date[] {
  const days: Date[] = [];
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const startPadding = firstDay.getDay();
  for (let i = startPadding - 1; i >= 0; i--) {
    days.push(new Date(year, month, -i));
  }

  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push(new Date(year, month, d));
  }

  // Only add padding to complete rows (not always 6 rows)
  const remainder = days.length % 7;
  if (remainder > 0) {
    const endPadding = 7 - remainder;
    for (let i = 1; i <= endPadding; i++) {
      days.push(new Date(year, month + 1, i));
    }
  }

  return days;
}

function getWeekDays(date: Date): Date[] {
  const days: Date[] = [];
  const dayOfWeek = date.getDay();
  const sunday = new Date(date);
  sunday.setDate(date.getDate() - dayOfWeek);

  for (let i = 0; i < 7; i++) {
    const d = new Date(sunday);
    d.setDate(sunday.getDate() + i);
    days.push(d);
  }
  return days;
}

function buildItemsMap(tasks: SidebarTask[], events: SidebarEvent[]): Map<string, DayItem[]> {
  const map = new Map<string, DayItem[]>();

  for (const task of tasks) {
    if (task.status === "done" || task.status === "archived") continue;
    // Use dueDate if available, otherwise fall back to scheduledDate
    const dateStr = task.dueDate ?? task.scheduledDate;
    if (!dateStr) continue;
    const date = new Date(dateStr);
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    const items = map.get(key) ?? [];
    items.push({ type: "task", data: task });
    map.set(key, items);
  }

  for (const event of events) {
    const date = new Date(event.startTime);
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    const items = map.get(key) ?? [];
    items.push({ type: "event", data: event });
    map.set(key, items);
  }

  return map;
}

/**
 * Formats a date as YYYY-MM-DD for API compatibility
 */
function formatDateISO(date: Date): string {
  return date.toISOString().split("T")[0];
}

export function CalendarPanel({
  tasks,
  events,
  onTaskClick,
  onEventClick,
  onDateRangeChange,
}: CalendarPanelProps): React.ReactElement {
  const [viewMode, setViewMode] = useState<ViewMode>("month");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const today = new Date();

  const itemsByDate = useMemo(() => buildItemsMap(tasks, events), [tasks, events]);

  const getItemsForDate = (date: Date): DayItem[] => {
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    return itemsByDate.get(key) ?? [];
  };

  // Navigation handlers
  const navigate = (delta: number) => {
    const newDate = new Date(currentDate);
    if (viewMode === "month") {
      newDate.setMonth(newDate.getMonth() + delta);
    } else if (viewMode === "week") {
      newDate.setDate(newDate.getDate() + delta * 7);
    } else {
      newDate.setDate(newDate.getDate() + delta);
    }
    setCurrentDate(newDate);
    setSelectedDate(null);

    // Notify parent of date range change for data fetching
    if (onDateRangeChange) {
      let startDate: string;
      let endDate: string;

      if (viewMode === "month") {
        // Get the visible month days (includes padding days)
        const monthDays = getMonthDays(newDate.getFullYear(), newDate.getMonth());
        startDate = formatDateISO(monthDays[0]);
        endDate = formatDateISO(monthDays[monthDays.length - 1]);
      } else if (viewMode === "week") {
        const weekDays = getWeekDays(newDate);
        startDate = formatDateISO(weekDays[0]);
        endDate = formatDateISO(weekDays[6]);
      } else {
        // Day view: same start and end
        startDate = formatDateISO(newDate);
        endDate = formatDateISO(newDate);
      }

      onDateRangeChange(startDate, endDate);
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Get days for current view
  const days = useMemo(() => {
    if (viewMode === "month") {
      return getMonthDays(currentDate.getFullYear(), currentDate.getMonth());
    } else if (viewMode === "week") {
      return getWeekDays(currentDate);
    } else {
      return [currentDate];
    }
  }, [viewMode, currentDate]);

  // Header label
  const headerLabel = useMemo(() => {
    if (viewMode === "month") {
      return currentDate.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    } else if (viewMode === "week") {
      const weekDays = getWeekDays(currentDate);
      const start = weekDays[0];
      const end = weekDays[6];
      if (start.getMonth() === end.getMonth()) {
        return `${start.toLocaleDateString(undefined, { month: "short" })} ${start.getDate()}-${end.getDate()}`;
      }
      return `${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} - ${end.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
    } else {
      return currentDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
    }
  }, [viewMode, currentDate]);

  const selectedItems = selectedDate ? getItemsForDate(selectedDate) : [];

  return (
    <div className="flex flex-col h-full">
      {/* View mode toggle */}
      <div className="flex items-center justify-center gap-1 mb-2">
        {(["day", "week", "month"] as ViewMode[]).map((mode) => (
          <button
            key={mode}
            type="button"
            onClick={() => {
              setViewMode(mode);
              setSelectedDate(null);
            }}
            className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
              viewMode === mode
                ? "bg-primary text-white"
                : "text-text-secondary hover:bg-surface-hover"
            }`}
          >
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </button>
        ))}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between px-1 py-1 mb-1">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-1 rounded-md hover:bg-surface-hover text-text-secondary"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-text-primary">{headerLabel}</span>
          <button
            type="button"
            onClick={goToToday}
            className="text-xs text-primary hover:underline"
          >
            Today
          </button>
        </div>

        <button
          type="button"
          onClick={() => navigate(1)}
          className="p-1 rounded-md hover:bg-surface-hover text-text-secondary"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Month/Week grid view */}
      {(viewMode === "month" || viewMode === "week") && (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Weekday headers */}
          <div className="grid grid-cols-7">
            {(viewMode === "month" ? WEEKDAYS_SHORT : WEEKDAYS_FULL).map((day, i) => (
              <div key={i} className="text-center text-[10px] md:text-xs font-medium text-text-tertiary py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Days grid - fills available space */}
          <div
            className="flex-1 grid grid-cols-7 gap-px bg-border/30"
            style={{
              gridTemplateRows: `repeat(${Math.ceil(days.length / 7)}, 1fr)`,
            }}
          >
            {days.map((date, idx) => {
              const isCurrentMonth = viewMode === "month" ? date.getMonth() === currentDate.getMonth() : true;
              const isToday = isSameDay(date, today);
              const isSelected = selectedDate && isSameDay(date, selectedDate);
              const items = getItemsForDate(date);
              const hasEvents = items.some((i) => i.type === "event");
              const hasTasks = items.some((i) => i.type === "task");

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    const dayItems = getItemsForDate(date);
                    // If only one item, open popover directly
                    if (dayItems.length === 1) {
                      const item = dayItems[0];
                      if (item.type === "task") {
                        onTaskClick(item.data);
                      } else {
                        onEventClick(item.data);
                      }
                    } else {
                      // Multiple items or none: show date selection
                      setSelectedDate(date);
                    }
                  }}
                  className={[
                    "flex flex-col items-center justify-center transition-colors bg-bg-secondary",
                    isCurrentMonth ? "text-text-primary" : "text-text-tertiary/50",
                    isSelected
                      ? "bg-primary text-white"
                      : isToday
                      ? "bg-surface ring-1 ring-inset ring-primary"
                      : "hover:bg-surface-hover",
                  ].join(" ")}
                >
                  <span className={`text-sm md:text-base ${isToday && !isSelected ? "font-bold" : ""}`}>
                    {date.getDate()}
                  </span>
                  {(hasEvents || hasTasks) && (
                    <div className="flex gap-0.5 md:gap-1 mt-1">
                      {hasEvents && (
                        <span className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${isSelected ? "bg-white" : "bg-primary"}`} />
                      )}
                      {hasTasks && (
                        <span className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${isSelected ? "bg-white/70" : "bg-text-tertiary"}`} />
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Day view - list of items */}
      {viewMode === "day" && (
        <div className="flex-1 overflow-y-auto">
          {(() => {
            const dayItems = getItemsForDate(currentDate);
            if (dayItems.length === 0) {
              return (
                <div className="flex items-center justify-center py-8">
                  <p className="text-sm text-text-tertiary">No items for this day</p>
                </div>
              );
            }
            return (
              <div className="space-y-1">
                {dayItems.map((item) => {
                  if (item.type === "task") {
                    return (
                      <button
                        key={`task-${item.data.id}`}
                        type="button"
                        onClick={() => onTaskClick(item.data)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-surface-hover text-sm flex items-center gap-2"
                      >
                        <span className="w-2 h-2 rounded-full bg-text-tertiary flex-shrink-0" />
                        <span className="truncate text-text-primary">{item.data.title}</span>
                      </button>
                    );
                  } else {
                    return (
                      <button
                        key={`event-${item.data.id}`}
                        type="button"
                        onClick={() => onEventClick(item.data)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-surface-hover text-sm flex items-center gap-2 border-l-2 border-primary"
                      >
                        <span className="truncate text-text-primary">{item.data.title}</span>
                        <span className="text-xs text-text-tertiary ml-auto flex-shrink-0">
                          {item.data.allDay ? "All day" : formatTime(item.data.startTime)}
                        </span>
                      </button>
                    );
                  }
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Selected day details (for month/week view) */}
      {(viewMode === "month" || viewMode === "week") && selectedDate && (
        <div className="mt-2 border-t border-border pt-2 max-h-32 overflow-y-auto flex-shrink-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-text-primary">
              {selectedDate.toLocaleDateString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
              })}
            </span>
            <button
              type="button"
              onClick={() => setSelectedDate(null)}
              className="text-[10px] text-text-tertiary hover:text-text-secondary"
            >
              ✕
            </button>
          </div>

          {selectedItems.length === 0 ? (
            <p className="text-xs text-text-tertiary py-2">No items</p>
          ) : (
            <div className="space-y-0.5">
              {selectedItems.map((item) => {
                if (item.type === "task") {
                  return (
                    <button
                      key={`task-${item.data.id}`}
                      type="button"
                      onClick={() => onTaskClick(item.data)}
                      className="w-full text-left px-2 py-1 rounded hover:bg-surface-hover text-xs flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-text-tertiary flex-shrink-0" />
                      <span className="truncate text-text-primary">{item.data.title}</span>
                    </button>
                  );
                } else {
                  return (
                    <button
                      key={`event-${item.data.id}`}
                      type="button"
                      onClick={() => onEventClick(item.data)}
                      className="w-full text-left px-2 py-1 rounded hover:bg-surface-hover text-xs flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      <span className="truncate text-text-primary">{item.data.title}</span>
                      <span className="text-[10px] text-text-tertiary ml-auto">
                        {item.data.allDay ? "All day" : formatTime(item.data.startTime)}
                      </span>
                    </button>
                  );
                }
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
